import {
  FluidFieldValidation,
  FluidFormElementState,
  FluidFormElementTranslationOptions,
  FluidFormState,
  FluidInputMaskingConfig,
  FluidLanguage,
  FluidPopoverConfig,
  FluidTextAreaConfig,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  FluidUpdateStrategy,
  generateUniqueId,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import {
  FormElementContainer,
  FormElementWrapper,
} from '../../common/fluid-common-form.components';
import { TEXT_AREA_EXCEEDS_MAX_LENGTH } from '../../constants/constants';
import { FluidFormElementsInterface } from '../../model/forms/fluid-form-elements.interface';
import {
  setI18nState,
  translateProperty,
  useI18n,
} from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  getFormInputId,
  getValue,
  handleAutoFocus,
  markTouchedAndValidate,
  reset,
  setValue,
  update,
  validateElement,
} from '../../utils/forms/form-method.utils';
import { setInitialElementState } from '../../utils/forms/form-utils';
import { Utils } from '../../utils/utils';

/**
 * @displayName Text Area
 *
 * @contextData FLUID Text Area is an input field for multi-line or long-form user input. For single line or short form entry, use FLUID Input Field. Unlike Input Field, Text Area supports text wrapping and can be resized vertically to accommodate any length of user input.
 * <br/><br/>
 * Text Area's default height can be adjusted using the "rows" Prop, where each "row" is equal to one line of text. Additionally, if user input overflows Text Area's current height, the component will automatically resize itself so that all content is visible without scrolling.
 *
 * @advancedUsage /forms
 *
 * @builder true
 */
@Component({
  tag: 'fluid-text-area',
  styleUrl: 'fluid-text-area.scss',
  shadow: true,
})
export class FluidTextArea
  implements
    ComponentInterface,
    FluidThemeInterface,
    FluidFormElementsInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * True if browser is IE11, false if any other browser.
   */
  private _browserIsMsie: boolean = Utils.browserIsMsie();

  /**
   * Reference to ResizeObserver instance that monitors changes to the
   * component's height & triggers an auto-resize when necessary.
   */
  private _resizeObserver: ResizeObserver;

  /**
   * Current height value of the <textarea> element's content + padding
   * (see "Element.scrollHeight" in MDN Web Docs for more information).
   */
  private _scrollHeight: number;

  /**
   * Prevents auto-resize when true. Set to true if <textarea> is
   * manually resized using the "drag handle" (bottom right-hand corner).
   */
  private _disableAutoResize = false;

  /**
   * Boolean that indicates whether the CSS property "field-sizing" is
   * supported by the browser. When true, pure-CSS solution is used for
   * "autoResize" functionality instead of JS.
   */
  private _cssResizeSupported: boolean = CSS.supports(
    'field-sizing',
    'content'
  );

  /**
   * Reference to setTimeout for "maskingConfig.blurDelay". Allows
   * timeout to be canceled if Text Area receives :focus before
   * callback is executed.
   */
  private _maskingDelay;

  /**
   * String that stores the field's "initialValue" AFTER it has been
   * validated against the "maxLength" Prop. Used in "reset" method
   * when "clear" param is false.
   */
  private _validatedInitialValue: string;

  // ================================================================ //
  // -- Validation Variables

  /**
   * Boolean that tracks whether the form field has received :focus and/or
   * been manipulated since initial page load.
   */
  touched = false;

  /**
   * Reference to the parent FLUID Form state.
   */
  _parentState: FluidFormState;

  /**
   * Reference to the core HTML <textarea> element.
   */
  _elementRef: HTMLTextAreaElement;

  /**
   * Boolean that tracks whether the form field has received autofocus
   */
  _autoFocus = false;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidTextAreaElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidFormElementTranslationOptions;
  _translateId: string;
  _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound: boolean;
  @State() _lang: FluidLanguage;

  // ================================================================ //
  // -- States

  /**
   * Derived form field state that tracks value, errors, validity, etc.
   * This common interface is shared among all form fields.
   */
  @State() _elementState: FluidFormElementState;

  /**
   * Boolean that controls whether masking is currently applied to
   * the form field's value.
   */
  @State() _maskingActive = false;

  /**
   * Number that tracks current character count in the embedded <textarea>.
   */
  @State() _currentCharCount: number;

  // ================================================================ //
  // -- Props

  /**
   * A unique name for the field (usually the dataKey for any form using
   * the input element).
   */
  @Prop({ mutable: true }) controlName: string;

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * When true, Text Area will automatically resize itself so that its
   * height is equal to the height of its content.
   */
  @Prop({ mutable: true }) autoResize = true;

  /**
   * If provided, a FLUID Form Label will be rendered above the Text
   * Area with the specified string.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the Form Label's text overflow behavior. By default, overflowing
   * label text will be truncated with ellipsis.
   */
  @Prop({ mutable: true }) labelTextOverflow: FluidTextOverflow;

  /**
   * Optional placeholder text displayed when the form field is empty.
   */
  @Prop({ mutable: true }) placeholder: string;

  /**
   * Removes the form field's default bottom margin when true.
   */
  @Prop({ mutable: true }) inlineField: boolean;

  /**
   * If true, displays a "clear" button that resets the form field when
   * clicked. Button is NOT displayed when field has "required" validation
   * or is empty.
   */
  @Prop({ mutable: true }) allowCancel: boolean;

  /**
   * If provided, populates the form field with a default value. NOTE:
   * this Prop takes precedence over the "value" Prop.
   */
  @Prop({ mutable: true }) initialValue: string;

  /**
   * Sets a maximum length (number of characters) allowed to be entered
   * in the Text Area & displays a character counter in the bottom
   * right-hand corner of the field.
   */
  @Prop({ mutable: true }) maxLength: number;

  @Watch('maxLength')
  onMaxLengthChanged() {
    this.initializeCharCount(true);
  }

  /**
   * When "maxLength" Prop is defined, setting this Prop to true will
   * disable the character counter.
   */
  @Prop({ mutable: true }) disableCharacterCount = false;

  /**
   * The Text Area's current value. This Prop is dynamically updated to
   * reflect the form field's current state, so it can be used as a
   * "synchronous" alternative to the public async "getValue()" method.
   * If provided on first load, populates the form field with a default
   * value. After the component is rendered, changes to this Prop will
   * update the form field's value (similar to "setValue()" method). NOTE:
   * "initialValue" will override this Prop on first load if both Props
   * are provided.
   */
  @Prop({ mutable: true }) value: string;

  @Watch('value')
  onValueChanged() {
    if (this.valueEqualsState()) return;

    this.touched = false;
    this._elementState = {
      ...this._elementState,
      value: this.validateValue(this.value),
      touched: this.touched,
    };

    this.validate().then();
    this.applyMasking(true);
    this.resetTextareaSize();
  }

  /**
   * If true, Text Area will be disabled & will not allow user input.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * If true, Text Area will not be mutable (i.e. user cannot edit the
   * form field's value). However, the form field will still allow :focus &
   * text selection.
   */
  @Prop({ mutable: true }) readonly = false;

  /**
   * An array of field validation options (see "Validation" & "Custom
   * Validation" tabs within the "Basic Forms Walkthrough" guide in the
   * FLUID Documentation Site for more information).
   */
  @Prop({ mutable: true }) validation: FluidFieldValidation[];

  /**
   * If provided, a secondary muted label will be displayed to the right
   * of the primary Form Label.
   */
  @Prop({ mutable: true }) supplementaryLabel: string;

  /**
   * The default height of the Text Area, where each "row" is equal to
   * one line of text.
   *
   * NOTE - Setting the rows property will disable the auto-resize functionality of textarea.
   */
  @Prop({ mutable: true, reflect: true }) rows: number;

  /**
   * When provided, enables masking of the form field's value when the
   * component loses :focus. Text Area will apply the specified
   * "maskingFn" to its text value after the amount of time specified in
   * "blurDelay" elapses (see "Form Field Masking" walkthrough guide in
   * the FLUID Documentation Site for more information).
   */
  @Prop({ mutable: true }) maskingConfig?: FluidInputMaskingConfig;

  /**
   * If provided, Text Area's Form Label will render a "helptext" icon
   * with attached Popover. To render a basic Popover with default
   * Prop values, simply provide the Popover's message as a string
   * value to this Prop. To render a Popover with customized Props,
   * provide a FluidPopoverConfig object to this Prop with the "message"
   * property set. For more information, please see Popover's API
   * documentation.
   */
  @Prop({ mutable: true }) helpText: string | FluidPopoverConfig;

  /**
   * Then true, this field will automatically have focus on page load.
   * Note: only one form field should have "autoFocus" set to true
   * on a page. Multiple fields with "autoFocus: true" can/will cause
   * unexpected behavior.
   */
  @Prop({ mutable: true }) autoFocus = false;

  /**
   * Determines the updating strategy for the field;
   * 'input' - the field will update the element state on each change to its value.
   * 'blur' - the field will update the element state when the user leaves the field.
   */
  @Prop({ mutable: true }) updateStrategy: FluidUpdateStrategy =
    FluidUpdateStrategy.INPUT;

  /**
   * Allows Text Area's Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidTextAreaConfig;

  @Watch('config')
  onConfigChanged() {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
  }

  // ================================================================ //
  // -- Events

  /**
   * Emits the element state when the form field's value changes (i.e.
   * fired on every keystroke).
   */
  @Event() valueChange: EventEmitter<FluidFormElementState>;

  // ================================================================ //
  // -- Testing Enablement

  /**
   * When supplied, will be prepended on each inner element of the form elemet Wrapper in order
   * to help facilitate
   */
  @Prop({ mutable: true }) identifier: string;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: selectiveMapConfigToLocal - sets local Props using "config" Prop values
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setRows - determines default value for "rows" Prop
   */
  connectedCallback(): void {
    Utils.selectiveMapConfigToLocal(this, this.config, ['value']);
    Utils.setGlobalTheme(this);
    this.setRows();
    useI18n.bind(this)({
      init: () => setI18nState.bind(this)(),
      language: () => setI18nState.bind(this)(),
    });
    this.identifier = this.identifier ?? this.host.id ?? generateUniqueId();
  }

  /**
   * Called once just after the component is first connected to the DOM.
   * @action: initializeState - sets the internal form element "state" & updates "value" Prop
   */
  componentWillLoad() {
    this.initializeState();
    this._autoFocus = this.autoFocus;
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action: resizeTextarea - updates the form field's height to match the height of its content
   * @action: initializeResizeObserver - begins monitoring form field for changes in size
   * @action: applyMasking - immediately applies masking to the form field (if "maskingConfig" provided)
   * @action: initializeCharCount - sets up <textarea> "value" property watcher for character counter
   */
  componentDidLoad() {
    this.resizeTextarea();
    this.initializeResizeObserver();
    this.applyMasking(true);
    this.initializeCharCount();
  }

  /**
   * Called AFTER every render().
   */
  componentDidRender() {
    handleAutoFocus(this);
  }

  /**
   * Called when component will be updated due to Prop or State change.
   * @action: updateValue - dynamically update the "value" Prop to match "_elementState.value"
   */
  componentWillUpdate() {
    this.updateValue();
  }

  /**
   * Called just after the component updates. It's never called during the first render().
   * @action: resizeTextarea - updates the form field's height to match the height of its content
   */
  componentDidUpdate() {
    this.resizeTextarea();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action: disconnect - stops ResizeObserver & removes instance
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Public Methods

  @Method() async setValue(value: any, opts?: Record<string, any>) {
    // If value is undefined, pass along to setValue() so reset() is still called
    const validatedValue = isNullOrUndefined(value)
      ? value
      : this.validateValue(value);
    return setValue(this, validatedValue, () => this.applyMasking(true), opts);
  }

  @Method() async getValue() {
    return getValue(this);
  }

  @Method() async validate() {
    return validateElement(this);
  }

  @Method() async reset(clear = false) {
    return reset(this, clear, () => {
      if (clear) return;
      this._elementState = setInitialElementState(
        this._validatedInitialValue,
        this.controlName
      );
    });
  }

  @Method() async markTouchedAndValidate() {
    return markTouchedAndValidate(this);
  }

  @Method() async setParentValue(formState: FluidFormState) {
    this._parentState = formState;
  }

  @Method() async getNativeInput() {
    return this._elementRef;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Determines default value for the "rows" Prop, which also sets the
   * default height of the form field (when empty).
   */
  setRows(): void {
    this.rows = this.inlineField ? 1 : this.rows ?? 0;
  }

  /**
   * Returns true if "value" Prop equals "_elementState.value" OR if
   * "_elementState.value" hasn't been initialized (i.e. is undefined) -
   * otherwise, returns false. Used to determine if "value" Prop is
   * out-of-sync with internal state.
   */
  valueEqualsState(): boolean {
    return (
      isNullOrUndefined(this._elementState?.value) ||
      this.value === this._elementState.value
    );
  }

  /**
   * Dynamically updates the "value" Prop to match "_elementState.value".
   * Called every time the component updates/re-renders.
   */
  updateValue(): void {
    if (this.valueEqualsState()) return;
    this.value = this._elementState.value;
  }

  /**
   * When "maxLength" Prop is set, validates that the provided value is
   * less than or equal to "maxLength". If not, outputs console warning &
   * truncates value to the number of characters specified by "maxLength".
   * Also transforms value of "null" or "undefined" into '' (empty string).
   * @param value - value from "initialValue" Prop, "value" Prop, "setValue()", etc.
   */
  validateValue(value: string): string {
    switch (true) {
      case isNullOrUndefined(value):
        return '';
      case !isNullOrUndefined(this.maxLength) && value.length > this.maxLength:
        Utils.consoleWarn(
          this.host,
          TEXT_AREA_EXCEEDS_MAX_LENGTH(this.maxLength)
        );
        return value.slice(0, this.maxLength);
      default:
        return value;
    }
  }

  /**
   * Sets up "pseudo-observer" that triggers update to character counter when
   * embedded <textarea> value changes programmatically (i.e. any update that
   * isn't from user input). Keeps character counter in-sync with <textarea>
   * value when "value" Prop changed, "setValue()" called, "clear" button
   * clicked, etc.
   * @param updateCharCount - triggers update to "_currentCharCount" when true
   */
  initializeCharCount(updateCharCount = false): void {
    if (
      isNullOrUndefined(this.maxLength) ||
      this.disableCharacterCount ||
      !this._elementRef
    )
      return;

    updateCharCount && this.updateCharCount();
    Utils.watchProperty(this._elementRef, 'value', (newVal) => {
      if (newVal?.length === this._currentCharCount) return;
      this.updateCharCount();
    });
  }

  /**
   * Updates the current character count based on the text content of the
   * embedded <textarea> element.
   */
  updateCharCount(): void {
    if (isNullOrUndefined(this.maxLength) || this.disableCharacterCount) return;
    this._currentCharCount = this._elementRef?.value?.length;
  }

  /**
   * Determines Text Area's default value (if provided), sets the initial
   * character count, initializes the Form Element's internal state, &
   * triggers update to "value" Prop.
   */
  initializeState(): void {
    this._validatedInitialValue = this.validateValue(
      this.initialValue ?? this.value
    );
    if (!isNullOrUndefined(this.maxLength) && !this.disableCharacterCount) {
      this._currentCharCount = this._validatedInitialValue.length;
    }
    this._elementState = setInitialElementState(
      this._validatedInitialValue,
      this.controlName
    );
    this.updateValue();
  }

  /**
   * Sets up ResizeObserver instance & begins monitoring the form field's
   * dimensions for changes in size. Each time the window is resized OR
   * a change in the surrounding UI causes Text Area's height to change,
   * the ResizeObserver triggers an automatic adjustment to the form field's
   * height. If a user MANUALLY resizes the <textarea> (using the "drag
   * handle" in the bottom right corner), the ResizeObserver will prevent
   * the component from auto-resizing until the next "blur" event occurs.
   */
  initializeResizeObserver(): void {
    if (
      !this.autoResize ||
      this._cssResizeSupported ||
      this._browserIsMsie ||
      typeof ResizeObserver === 'undefined'
    )
      return;

    this._resizeObserver = new ResizeObserver(
      Utils.debounce((entries) => {
        if (entries.length !== 1) return;
        const borderBoxHeight = entries[0]?.borderBoxSize[0]?.blockSize;

        switch (true) {
          // Terminate if <textarea> element's height isn't reported
          case !borderBoxHeight:
            return;
          // If the <textarea> was manually resized, disable auto-resize until next "blur" event
          case !!this._scrollHeight && borderBoxHeight !== this._scrollHeight:
            this._disableAutoResize = true;
            break;
          // If no other conditions are true, then UI has been resized & we need to update <textarea> height
          default:
            this.resizeTextarea();
        }
      })
    );

    this._resizeObserver.observe(this._elementRef);
  }

  /**
   * Determines the <textarea> element's "scrollHeight" (i.e. true
   * height of the form field's text value + top/bottom padding), then
   * updates the element's inline "height" style based on this value.
   * This ensures that the form field's entire text value is always
   * visible (without scrolling), AND ensures that its height isn't
   * taller than the height of its text content.
   */
  resizeTextarea(): void {
    if (
      !this._elementRef ||
      !this.autoResize ||
      this._cssResizeSupported ||
      this._disableAutoResize
    )
      return;

    this._elementRef.style.height = '';
    this._scrollHeight = this._elementRef.scrollHeight;
    this._elementRef.style.height = this._scrollHeight
      ? `${this._scrollHeight}px`
      : '';
  }

  /**
   * Re-enables auto-resize functionality (if previously disabled due
   * to manual resize) & resizes <textarea> element. Called when the
   * form field loses :focus, "cancelInput" button is clicked, or "value"
   * Prop is updated.
   */
  resetTextareaSize(): void {
    if (!this._cssResizeSupported) {
      this._disableAutoResize = false;
      this.resizeTextarea();
    } else if (this.autoResize) {
      this._elementRef.style.height = '';
    }
  }

  /**
   * Stops masking delay timeout & resets masking state.
   */
  clearMasking(): void {
    clearTimeout(this._maskingDelay);
    this._maskingActive = false;
  }

  /**
   * Applies masking to Text Area after specified delay (on blur) OR
   * immediately (on first load).
   * @param immediate - skips "blurDelay" timeout if true
   */
  applyMasking(immediate = false): void {
    if (!this.maskingConfig || !this._elementState?.value) return;

    this.clearMasking();
    this._maskingDelay = setTimeout(
      () => {
        this._maskingActive = true;
      },
      immediate ? 0 : this.maskingConfig.blurDelay ?? 0
    );
  }

  /**
   * Gets string to be displayed within <textarea> element. Returns
   * masked value (if "maskingConfig" provided) or "_elementState.value".
   */
  getTextareaDisplayValue(): string {
    if (!this._elementState?.value) return;

    if (!!this.maskingConfig && this._maskingActive) {
      const { maskingFn } = this.maskingConfig;
      return maskingFn
        ? maskingFn(this._elementState.value)
        : this._elementState.value;
    } else {
      return this._elementState.value;
    }
  }

  /**
   * Removes masking / stops masking delay timeout when form field
   * receives :focus.
   */
  handleFocus(): void {
    if (this.disabled || this.readonly) return;
    this.clearMasking();
  }

  /**
   * Resets Text Area height, validates form field value, & applies
   * masking (if "maskingConfig" provided) when field loses :focus.
   */
  handleBlur(event: any): void {
    if (this.disabled || this.readonly) return;
    this.resetTextareaSize();
    if (this.updateStrategy?.toUpperCase() === FluidUpdateStrategy.BLUR) {
      update(this, event);
    }
    this.markTouchedAndValidate().then(() => {
      this.applyMasking();
    });
  }

  /**
   * Updates internal state value when Text Area's value changes (if
   * "updateStrategy" Prop is NOT "blur") & triggers update to
   * "_currentCharCount".
   * @param event - the InputEvent fired when <textarea> value changes
   */
  handleInput(event: InputEvent): void {
    if (this.disabled || this.readonly) return;

    this.updateCharCount();
    if (this.updateStrategy?.toUpperCase() !== FluidUpdateStrategy.BLUR) {
      update(this, event);
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <FormElementWrapper
        host={this}
        maxLength={this.maxLength}
        disableCharacterCount={this.disableCharacterCount}
        currentCharCount={this._currentCharCount}>
        <FormElementContainer
          host={this}
          onCancel={() => this.resetTextareaSize()}>
          <textarea
            data-test-id={`${this.identifier}-text-area-input`}
            ref={(el) => (this._elementRef = el)}
            id={getFormInputId(this.config?.id, this.controlName)}
            aria-describedby={this.maxLength ? 'sr-char-count' : null}
            rows={this.rows}
            name={this._elementState.name}
            placeholder={translateProperty.bind(this)('placeholder')}
            disabled={this.disabled}
            readOnly={this.readonly}
            maxLength={this.maxLength || null}
            value={this.getTextareaDisplayValue()}
            onInput={(e: InputEvent) => this.handleInput(e)}
            onFocus={() => this.handleFocus()}
            onBlur={(e: FocusEvent) => this.handleBlur(e)}
            class={{
              'text-area-input': true,
              'auto-resize': this.autoResize && this.rows === 0,
              'read-only': this.readonly,
              disabled: this.disabled,
            }}
          />
        </FormElementContainer>
      </FormElementWrapper>
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * @internal
   */
  @Prop({ mutable: true }) _standalone = true;

  /**
   * @internal
   */
  @Prop({ mutable: true }) parentState: FluidFormState;

  /**
   * @internal
   */
  @Prop({ mutable: true }) externalState: Record<string, any>;

  /**
   * @internal
   */
  @Prop({ mutable: true }) autoValidateChildren: boolean;

  @Watch('parentState') async onParentStateChange(newState: FluidFormState) {
    this._parentState = newState;
    if (this.autoValidateChildren) {
      await this.validate();
    }
  }
}
